import React from "react";
import styled from "styled-components";
import PerformanceValue from "../../../performance/PerformanceValue";
import {kpiBriefDescription} from "../../../performance/Kpi";
import {Direction, DirectionColumn} from "../../idea/Direction";
import {StockColumn, StockDetailsContainer, StockName, StockTicker} from "../../idea/Stock";
import {IdeaKpiCell, IdeaKpiColumn} from "./IdeaKpi";
import {logAction} from "../../../../logging/logEvent";
import {Option} from "../option/Option";
import {Column} from "react-virtualized";
import IdeasTable from "../../idea/IdeasTable";
import {Title} from "../../Sections";
import {RightAlignedSortableCell, SortableHeading} from "../../../sorting/SortableHeading";
import {sortBasedOnState} from "../../../sorting/sort";
import {TimeOpenColumn, TimeOpenValue} from "./PortfolioTimeOpen";
import {CentredLoader} from "../../../elements/Loader";
import {Price, PriceHeader} from "../../idea/Price";
import {Tooltip} from "antd";
import {scaledValue} from "../../../../components/performance/CompactPerformanceValue";
import {InvestmentCell, InvestmentColumn} from "./Investment";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-width: fit-content;
`;

const TitleBar = styled.div`
    display: flex;  
    height: 46px;
    flex-shrink: 0;
    border-bottom: 1px solid #d6d0c8; 
`;

const Options = styled.div`
    display: flex;    
`;

const IdeasForPortfolioTitle = styled(Title)`
    display: flex;
    flex-grow: 1;
    align-items: center; 
`;

const NoIdeasMessage = styled.div`
    margin-top: 8px;
`;

const PriceTooltip = styled.span`
    text-decoration: underline;
    text-decoration-style: dashed;
`;

const PriceTooltipMessage = styled.div`
    white-space: pre;
`;

const startPriceTooltip =
`If the idea was opened within the period,
then this is its entry price.
 
If the idea was opened before the period,
then this is the last end-of-day price
before the performance period start.`;

const endPriceToolTip =
`If the idea was closed within the period,
then this is its close price.

If the idea was still open at the end the period,
then this is the end-of-day price from the last
trading day of the performance period.`;


function headerWithToolTip({heading, content, className, onMouseOver}) {
    return (<Tooltip title={content} mouseLeaveDelay={0} placement="bottom" overlayClassName="t-formatted-tooltip">
        <PriceTooltip className={className} onMouseEnter={onMouseOver}>
            {heading}
        </PriceTooltip>
    </Tooltip>);
}

function priceColumn({header, headerClassName, dataKey, className, valueFrom}) {
    return (<Column key={dataKey.includes("End") ? 5 : 4}
                    dataKey={dataKey}
                    minWidth={70}
                    width={100}
                    flexShrink={1}
                    headerRenderer={() => (<PriceHeader className={headerClassName}>{header}</PriceHeader>)}
                    cellRenderer={({rowData}) => (
                        <Price className={className} price={valueFrom(rowData)} currency={rowData.stockCurrency}/>
                    )}/>);
}


const PortfolioIdeasTable = ({ideasList,
                              unifiedIdeasView,
                              ideaActions,
                              ideaKpi,
                              kpi,
                              performanceSettings,
                              performancePeriod,
                              performancePeriodStart,
                              sortIdeasFunction,
                              sort,
                              hasPercentReturnAutoCloseTrigger}) => {

    function onRowClick({rowData}) {
        const ideaId = rowData.ideaId;
        if (!ideaId) {
            return;
        }

        logAction({
            type: "AUTHOR_IDEA_ROW_CLICKED",
            ideaId: ideaId,
            ideasView: unifiedIdeasView
        });
        ideaActions.showDetail(ideaId);
    }

    const rowStyle = ({index}) => {
        if (index !== -1 && !ideasList[index].ideaId) {
            return {
                backgroundColor: 'white',
                cursor: 'not-allowed'
            };
        }
    };

    const IdeasForPortfolioHeading = (props) => (
        <SortableHeading {...props}
                         sortState={sort}
                         tableName="portfolio_ideas"
                         sortByFieldFn={sortIdeasFunction}/>
    );

    const logMouseOver = (tooltipName) => {
        logAction({
            type: "AuthorMouseOverTooltip",
            tooltipName,
            ideasView: unifiedIdeasView,
            performancePeriod,
            performancePeriodStart,
        });
    };

    function timeOpenColumn({dataKey, width, fieldName, heading, className}) {
        return <Column key={3}
                       dataKey={dataKey}
                       width={width}
                       flexShrink={0}
                       headerRenderer={() => (
                           <TimeOpenColumn>
                               <IdeasForPortfolioHeading horizontalAlignment="flex-end" fieldName={fieldName}>
                                   {heading}
                               </IdeasForPortfolioHeading>
                           </TimeOpenColumn>
                       )}
                       cellRenderer={({cellData}) => (
                           <RightAlignedSortableCell>
                               <TimeOpenValue className={className}>{cellData}</TimeOpenValue>
                           </RightAlignedSortableCell>
                       )}/>;
    }

    const columns = [];
    columns.push(<Column key={1}
                         dataKey="direction"
                         flexShrink={0}
                         width={50}
                         headerRenderer={() => (
                             <DirectionColumn>
                                 <IdeasForPortfolioHeading descendingFirst={false} fieldName="direction">
                                     L/S
                                 </IdeasForPortfolioHeading>
                             </DirectionColumn>
                         )}
                         cellRenderer={({cellData}) => (
                             <Direction direction={cellData}/>
                         )}/>);
    columns.push(<Column key={2}
                         dataKey="bloomberg"
                         minWidth={80}
                         width={116}
                         flexGrow={2}
                         flexShrink={1}
                         headerRenderer={() => (
                             <StockColumn>
                                 <IdeasForPortfolioHeading descendingFirst={false} fieldName="bloomberg">
                                     Stock
                                 </IdeasForPortfolioHeading>
                             </StockColumn>
                         )}
                         cellRenderer={({rowData}) => (
                             <StockDetailsContainer className="t-stock">
                                 <StockTicker className="t-stock-bloomberg">{rowData.bloomberg}</StockTicker>
                                 <StockName className="t-stock-name">{rowData.stockName}</StockName>
                             </StockDetailsContainer>
                         )}/>);

    columns.push(unifiedIdeasView === "previous-ideas" || unifiedIdeasView === "closed-ideas" ?
        timeOpenColumn({dataKey: "timeOpenInPeriod", width: 120, fieldName: "timeOpenInPeriodMillis", heading: "Time Open In Period", className: "t-time-open-in-period"})
        : timeOpenColumn({dataKey: "timeOpen", width: 94, fieldName: "timeOpenMillis", heading: "Time Open", className: "t-time-open"}));

    if (kpi) {
        columns.push(priceColumn({
            header: headerWithToolTip({
                heading: "Start Price",
                content: <PriceTooltipMessage>{startPriceTooltip}</PriceTooltipMessage>,
                className: "t-periodStartPrice-heading",
                onMouseOver: () => logMouseOver("period start price")
            }),
            dataKey: "periodStartStockPrice",
            valueFrom: (rowData) => (rowData.kpiValue !== null || rowData.endPrice !== null) ? rowData.startPrice : null,
            className: "t-periodStartStockPrice"
        }));

        if (unifiedIdeasView === "closed-ideas") {
            columns.push(priceColumn({
                header: "Close Price",
                headerClassName: "t-periodEndPrice-heading",
                dataKey: "periodEndStockPrice",
                valueFrom: (rowData) => rowData.endPrice,
                className: "t-periodEndStockPrice"
            }));
        } else if (unifiedIdeasView === "previous-ideas") {
            columns.push(priceColumn({
                header: headerWithToolTip({
                    heading: "End Price",
                    content: <PriceTooltipMessage>{endPriceToolTip}</PriceTooltipMessage>,
                    className: "t-periodEndPrice-heading",
                    onMouseOver: () => logMouseOver("period end price")
                }),
                dataKey: "periodEndStockPrice",
                valueFrom: (rowData) => rowData.endPrice,
                className: "t-periodEndStockPrice"
            }));
        }
    }

    if (unifiedIdeasView === "open-ideas") {
        columns.push(
            <Column key={6}
                    dataKey="investment"
                    minWidth={60}
                    width={90}
                    flexShrink={0}
                    headerRenderer={() => (
                        <InvestmentColumn className={"t-investment-heading"}>
                            <IdeasForPortfolioHeading horizontalAlignment="flex-end" fieldName="currentInvestment">Investment</IdeasForPortfolioHeading>
                        </InvestmentColumn>)}
                    cellRenderer={({rowData}) => (
                        <RightAlignedSortableCell>
                            <InvestmentCell className="t-investment">{scaledValue(rowData.currentInvestment)} {rowData.investmentCurrency}</InvestmentCell>
                        </RightAlignedSortableCell>
                    )}/>);
    }

    if (kpi) {
        columns.push(
            <Column key={7}
                    dataKey="kpiValue"
                    width={100}
                    flexShrink={0}
                    headerRenderer={() => (
                        <IdeaKpiColumn>
                            <IdeasForPortfolioHeading horizontalAlignment="flex-end" fieldName="kpiValue">
                                {kpiBriefDescription(ideaKpi, performanceSettings.currency)}
                            </IdeasForPortfolioHeading>
                        </IdeaKpiColumn>
                    )}
                    cellRenderer={({cellData}) => (
                        <IdeaKpiCell className="t-kpi-value">
                            <RightAlignedSortableCell>
                                {cellData === null ? "awaiting price" :
                                    <PerformanceValue name={ideaKpi} value={cellData}/>}
                            </RightAlignedSortableCell>
                        </IdeaKpiCell>
                    )}/>);
    }

    if (unifiedIdeasView === "open-ideas" && hasPercentReturnAutoCloseTrigger && ideaKpi && !ideaKpi.includes("Percent")) {
        const dataKey = "returnPercentForAutoClose";
        const headingKey = `${ideaKpi.includes("abs") ? "absolute" : "relative"}ReturnPercent`;
        columns.push(
            <Column key={8}
                    dataKey={dataKey}
                    width={70}
                    flexShrink={0}
                    headerRenderer={() => (
                        <IdeaKpiColumn>
                            <IdeasForPortfolioHeading horizontalAlignment="flex-end" fieldName={headingKey}>
                                {kpiBriefDescription(headingKey)}
                            </IdeasForPortfolioHeading>
                        </IdeaKpiColumn>
                    )}
                    cellRenderer={({cellData}) => (
                        <IdeaKpiCell className={`t-${dataKey}-value`}>
                            <RightAlignedSortableCell>
                                {cellData === null ? "awaiting price" :
                                    <PerformanceValue name={dataKey} value={cellData}/>}
                            </RightAlignedSortableCell>
                        </IdeaKpiCell>
                    )}/>);
    }

    return (
        <IdeasTable ideas={ideasList} onRowClick={onRowClick} rowStyle={rowStyle}>
            {columns}
        </IdeasTable>
    );
};


function emptyListMessage(unifiedIdeasView, kpi) {
    if (unifiedIdeasView === "previous-ideas") {
        return "No ideas open during this period";
    }
    if (unifiedIdeasView === "closed-ideas") {
        return "No ideas closed during this period";
    }
    return "No ideas entered for this period";
}

export const IdeasForPortfolio = ({
                                      portfolioAuthor: {authorName, openIdeas, closedIdeas, previousIdeas, authorId, ideaKpi, kpi, performanceSettings, performancePeriod, performancePeriodStart},
                                      selectedAuthorId,
                                      portfolioAuthorActions,
                                      lookingAtSomeoneElse,
                                      ideaActions,
                                      ideasView,
                                      openIdeasSort,
                                      closedIdeasSort,
                                      previousIdeasSort,
                                      hasPercentReturnAutoCloseTrigger
                                  }) => {
    const isLoading = authorId && authorId !== selectedAuthorId;
    if (isLoading) {
        return <CentredLoader/>;
    }

    let ideasList;
    let sort;
    let sortIdeasFunction;
    let unifiedIdeasView;

    if (previousIdeas) {
        sort = previousIdeasSort;
        sortIdeasFunction = portfolioAuthorActions.sortPreviousIdeasBy;
        ideasList = sortBasedOnState(previousIdeas, sort);
        unifiedIdeasView = "previous-ideas";
    } else {
        if (ideasView === "open-ideas") {
            sort = openIdeasSort;
            sortIdeasFunction = portfolioAuthorActions.sortOpenIdeasBy;
            ideasList = sortBasedOnState(openIdeas, sort);
        } else {
            sort = closedIdeasSort;
            sortIdeasFunction = portfolioAuthorActions.sortClosedIdeasBy;
            ideasList = sortBasedOnState(closedIdeas, sort);
        }
        unifiedIdeasView = ideasView;
    }

    const headerText = lookingAtSomeoneElse ? `${authorName}'s Ideas` : "Ideas";

    return <Container className="t-ideas-for-portfolio">
        <TitleBar>
            <IdeasForPortfolioTitle className="t-ideas-for-portfolio-title">
                <div>{headerText}</div>
            </IdeasForPortfolioTitle>
            {
                unifiedIdeasView !== "previous-ideas" ? (
                    <Options className="t-options">
                        <Option className="t-open-ideas"
                                selected={ideasView === "open-ideas"}
                                text="Open"
                                onClick={() => portfolioAuthorActions.changeIdeasView("open-ideas")}/>
                        <Option className="t-closed-ideas"
                                selected={ideasView === "closed-ideas"}
                                text="Closed"
                                onClick={() => portfolioAuthorActions.changeIdeasView("closed-ideas")}/>
                    </Options>
                ) : null
            }
        </TitleBar>

        {
            (!ideasList || ideasList.length === 0)
            ? <NoIdeasMessage>{emptyListMessage(unifiedIdeasView, kpi)}</NoIdeasMessage>
            : <PortfolioIdeasTable ideaKpi={ideaKpi}
                                   kpi={kpi}
                                   performanceSettings={performanceSettings}
                                   performancePeriod={performancePeriod}
                                   performancePeriodStart={performancePeriodStart}
                                   ideasList={ideasList}
                                   unifiedIdeasView={unifiedIdeasView}
                                   sortIdeasFunction={sortIdeasFunction}
                                   sort={sort}
                                   ideaActions={ideaActions}
                                   hasPercentReturnAutoCloseTrigger={hasPercentReturnAutoCloseTrigger}/>
        }
    </Container>;
};
